import { type QueryFilters, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { emitCoverageRefresh } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { deleteUpload, restoreUpload, updateUploadTags } from '@/features/uploads/data';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { buildUploadMutationInvalidations, queryKeys, useInvalidateQueries } from '@/shared/hooks';
import {
  gridPaths,
  invalidateExecutionRuns,
  invalidateGridQuery,
  invalidateResultsFiles,
  invalidateUploads,
} from '@/shared/hooks/queryKeys';
import type { UploadDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
  purpose: UploadDto['purpose'];
};

type UseUploadsTableRowActionsParams = { selectedProjectId: string | null };

type UseUploadsTableRowActionsResult = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: UploadDto) => void;
  onRestoreRow: (row: UploadDto) => void;
  restoreLoading: boolean;
  restoreRowPendingId: string | null;
  onUpdateRowTagsColor: (input: { row: UploadDto; tags: string[]; colors: TagColor[] }) => void;
  updateRowTagsColorPendingId: string | null;
};

const refetchCoverageQueries = async ({
  queryClient,
  selectedOrganisationId,
  selectedProjectId,
  uploadProjectId,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  uploadProjectId: string | null;
}): Promise<void> => {
  const projectIds = Array.from(
    new Set(
      [selectedProjectId, uploadProjectId].filter(
        (projectId): projectId is string => typeof projectId === 'string' && projectId.length > 0,
      ),
    ),
  );
  const refetches = [
    queryClient.refetchQueries({
      queryKey: queryKeys.coverageUploads(selectedOrganisationId, selectedProjectId),
      exact: false,
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.coverageRuns(selectedOrganisationId, selectedProjectId),
      exact: false,
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.coverageRunSummary(selectedOrganisationId, selectedProjectId),
      exact: false,
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.coverageFiles(selectedOrganisationId, selectedProjectId),
      exact: false,
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.dataGrid(gridPaths.uploads),
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.dataGrid(gridPaths.coverageRuns),
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.dataGrid(gridPaths.coverageFiles),
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.dataGrid('coverage-runs/local-table'),
      type: 'active',
    }),
    queryClient.refetchQueries({
      queryKey: queryKeys.dataGrid('coverage-files/local-table'),
      type: 'active',
    }),
  ];

  if (selectedProjectId === null) {
    refetches.push(
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageUploads(selectedOrganisationId, null),
        exact: false,
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageRuns(selectedOrganisationId, null),
        exact: false,
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageRunSummary(selectedOrganisationId, null),
        exact: false,
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageFiles(selectedOrganisationId, null),
        exact: false,
        type: 'active',
      }),
    );
  }

  projectIds.forEach((projectId) => {
    refetches.push(
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageUploads(selectedOrganisationId, projectId),
        exact: false,
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageRuns(selectedOrganisationId, projectId),
        exact: false,
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageRunSummary(selectedOrganisationId, projectId),
        exact: false,
        type: 'active',
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.coverageFiles(selectedOrganisationId, projectId),
        exact: false,
        type: 'active',
      }),
    );
  });

  await Promise.all(refetches);
};

export const useUploadsTableRowActions = ({
  selectedProjectId,
}: UseUploadsTableRowActionsParams): UseUploadsTableRowActionsResult => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [restoreRowPendingId, setRestoreRowPendingId] = useState<string | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const deleteUploadMutation = useMutation({
    mutationFn: async (input: { id: string }) => await deleteUpload(input, selectedOrganisationId),
    onSuccess: async (_deleted, input) => {
      await invalidateQueries(
        buildUploadMutationInvalidations({
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
          uploadProjectId: deleteTarget?.projectId ?? null,
          purpose: deleteTarget?.purpose ?? null,
        }),
      );
      if (deleteTarget?.purpose === 'coverage') {
        await refetchCoverageQueries({
          queryClient,
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
          uploadProjectId: deleteTarget.projectId,
        });
        emitCoverageRefresh();
      }
      notify({
        message: getToastMessage('entity.deleted', {
          entity: 'upload',
          name: deleteTarget?.name ?? input.id,
        }),
        tone: 'green',
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const restoreUploadMutation = useMutation({
    mutationFn: async (input: { id: string; projectId: string; purpose: UploadDto['purpose'] }) =>
      await restoreUpload({ id: input.id }, selectedOrganisationId),
    onSuccess: async (_restored, input) => {
      await invalidateQueries(
        buildUploadMutationInvalidations({
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
          uploadProjectId: input.projectId,
          purpose: input.purpose,
        }),
      );
      if (input.purpose === 'coverage') {
        await refetchCoverageQueries({
          queryClient,
          selectedOrganisationId: selectedOrganisationId ?? null,
          selectedProjectId,
          uploadProjectId: input.projectId,
        });
        emitCoverageRefresh();
      }
      notify({
        message: getToastMessage('entity.restored', { entity: 'upload', name: input.id }),
        tone: 'green',
      });
      setRestoreRowPendingId(null);
    },
    onError: (error) => {
      setRestoreRowPendingId(null);
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onDeleteRow = useCallback((row: UploadDto) => {
    setDeleteTarget({
      id: row.id,
      name: row.fileName,
      projectId: row.projectId,
      purpose: row.purpose,
    });
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteUploadMutation.isPending) return;
    setDeleteTarget(null);
  }, [deleteUploadMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteUploadMutation.mutate({ id: deleteTarget.id });
  }, [deleteTarget, deleteUploadMutation]);

  const onRestoreRow = useCallback(
    (row: UploadDto) => {
      setRestoreRowPendingId(row.id);
      restoreUploadMutation.mutate({
        id: row.id,
        projectId: row.projectId,
        purpose: row.purpose,
      });
    },
    [restoreUploadMutation],
  );

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: UploadDto; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateUploadTags({ id: row.id, tags: normalizedTags }, selectedOrganisationId);
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
          });
          const invalidations: QueryFilters[] = [
            invalidateUploads(selectedOrganisationId ?? null, selectedProjectId ?? null),
            invalidateResultsFiles(selectedOrganisationId ?? null, selectedProjectId ?? null),
            invalidateExecutionRuns(selectedOrganisationId ?? null, selectedProjectId ?? null),
            invalidateGridQuery(gridPaths.uploads),
            invalidateGridQuery(gridPaths.resultsFiles),
            invalidateGridQuery(gridPaths.executionRuns),
            invalidateGridQuery(gridPaths.suiteRuns),
            invalidateGridQuery(gridPaths.testRuns),
          ];
          await invalidateQueries(invalidations);
          await queryClient.refetchQueries({ type: 'active' });
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      invalidateQueries,
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateTagColorMutation,
    ],
  );

  return {
    deleteLoading: deleteUploadMutation.isPending,
    deleteRowPendingId: deleteUploadMutation.isPending ? (deleteTarget?.id ?? null) : null,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onRestoreRow,
    restoreLoading: restoreUploadMutation.isPending,
    restoreRowPendingId,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId: updatingTagRowId,
  };
};
